import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, OnInit, OnDestroy } from '@angular/core';
import { Observable } from 'rxjs';
import { SubSink } from 'subsink';
import { LDFlagValue } from 'launchdarkly-js-client-sdk';
import { dateOptions, stringOptions,intOptions } from '../../conditions/conditions.constants';
import { DatatableColumn } from 'src/app/shared/models/components.models';
import { createdbyRegex, modifiedbyRegex, datecreatedRegex, datemodifiedRegex  } from '../datatable.constants';
import { LaunchDarklyService } from 'src/app/core/services/launchdarkly.service';
import { FEATURE_FLAGS } from '../../../launchdarkly.constants';

@Component({
  selector: 'app-column-filter',
  templateUrl: './column-filter.component.html',
  styleUrls: ['./column-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.accessibility-enabled]': 'enableDataTableAccessibilityValue' }
})
export class ColumnFilterComponent implements OnInit, OnDestroy {
  @Input() filterCol: DatatableColumn | any;
  @Input() allowColumnFilter: boolean = false;
  @Input() loadExternalUsers: boolean = false;
  @Input() tableId: string = '';
  @Input() bulkUpdateTableName: string = '';

  @Output() onFilterApply = new EventEmitter<DatatableColumn>();
  @Output() onFilterClear = new EventEmitter<DatatableColumn>();
  @Output() onScrollSelect = new EventEmitter<any>();

  dateOptions = dateOptions;
  stringOptions = stringOptions;
  integerOptions = intOptions;
  createdbyRegex = createdbyRegex;
  modifiedbyRegex = modifiedbyRegex;
  datecreatedRegex = datecreatedRegex;
  datemodifiedRegex = datemodifiedRegex;

  // Feature flag for accessibility
  enableDataTableAccessibility$!: Observable<LDFlagValue>;
  enableDataTableAccessibilityValue: boolean = false;
  private subsink = new SubSink();

  constructor(private ldService: LaunchDarklyService) {}

  ngOnInit(): void {
    // Initialize feature flag observable
    this.enableDataTableAccessibility$ = this.ldService.getFlagValue(
      FEATURE_FLAGS.ENABLE_DATA_TABLE_ACCESSIBILITY,
      false
    );
    
    // Subscribe to get synchronous value for logic
    this.subsink.add(
      this.enableDataTableAccessibility$.subscribe((value) => {
        this.enableDataTableAccessibilityValue = !!value;
      })
    );
  }

  ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }

  /**
   * Set the filter condition for the column
   */
  setCondition(event: any, filterCol: DatatableColumn): void {
    filterCol.filterCondition = event?.[0]?.value;
  }

  /**
   * Set the filter value for the column
   */
  setValue(event: any): void {
    this.filterCol.filterValue = event.target.value;
  }

  /**
   * Get the input type for date-related filters
   */
  getDateInputType(filterCol: DatatableColumn): string {
    if (filterCol.filterCondition === 'month') return 'month';
    if (filterCol.filterCondition === 'week') return 'week';
    if (filterCol.fieldType === 'DATETIME') return 'datetime-local';
    return 'date';
  }

  /**
   * Get the minimum value for date inputs
   */
  getMinValue(type: string): string | null {
    if (type === 'date') return '1900-01-01';
    else if (type === 'datetime-local') return '1900-01-01T00:00';
    return null;
  }

  /**
   * Get the maximum value for date inputs
   */
  getMaxValue(type: string): string | null {
    if (type === 'date') return '9999-12-31';
    else if (type === 'datetime-local') return '9999-12-31T23:59';
    return null;
  }

  /**
   * Clear the filter for the column
   * Bifurcated based on feature flag
   */
  clearFilterForCol(filterCol: DatatableColumn): void {
    filterCol.filterValue = (filterCol.fieldType === 'USER' || filterCol.fieldType === 'ENUM' || filterCol.fieldType === 'ENUM2' || filterCol.isRelation) ? [] : '';
    // Bifurcate: FLAG OFF uses '', FLAG ON uses null
    filterCol.filterCondition = this.enableDataTableAccessibilityValue ? null : '';
    filterCol.filterValue2 = '';
    if ((filterCol.fieldType === 'INT' && !filterCol.isRelation) || filterCol.fieldType === 'DOUBLE' || filterCol.fieldType === 'DECIMAL(20,2)') {
      filterCol.minValue = +filterCol.minValue;
      filterCol.maxValue = +filterCol.maxValue;
      filterCol.filterValue = filterCol.minValue;
      filterCol.filterValue2 = filterCol.maxValue;
    }
    this.onFilterClear.emit(filterCol);
  }

  /**
   * Apply the selected filter for the column
   */
  selectFilterSelected(filterCol: DatatableColumn): void {
    this.onFilterApply.emit(filterCol);
  }

  /**
   * Check if the "Apply" button should be disabled
   */
  isFilterApplyDisabled(filterCol: DatatableColumn): boolean {
    if (!this.isStringField(filterCol)) {
      if (filterCol?.filterCondition === 'empty') return false;
      if (!filterCol?.filterCondition || !filterCol?.filterValue) return true;
    }
    if (filterCol?.fieldType === 'DATE' || filterCol?.fieldType === 'DATETIME' || this.datecreatedRegex.test(filterCol.fieldName) || this.datemodifiedRegex.test(filterCol.fieldName)) {
      if (filterCol?.filterCondition === 'today') return false;
      if (!filterCol?.filterCondition || !filterCol?.filterValue) return true;
    }
    if (filterCol?.fieldType === 'ENUM' || filterCol?.fieldType === 'ENUM2' || filterCol?.fieldType === 'USER' || filterCol?.isRelation) {
      if (!filterCol.filterValue.length || (filterCol?.filterValue?.length == 1 && filterCol?.filterValue?.[0] == '' && this.bulkUpdateTableName !== 'Fields')) return true;
    }
    return false;
  }

  /**
   * Check if the column is a string field
   */
  isStringField(col:any){
    const stringFieldTypes = ['INT','DOUBLE','DECIMAL(20,2)','TINYINT(1)','DATE','DATETIME','USER','ENUM','ENUM2'];
    return stringFieldTypes.includes(col?.fieldType) || col?.isRelation;
  }

  /**
   * Handle slider value changes
   */
  onSliderValueChange(event: any): void {
    this.filterCol.filterValue = event.minvalue;
    this.filterCol.filterValue2 = event.maxvalue;
  }

  /**
   * Handle checkbox value changes
   */
  changeCheckboxValue(event: any, isTrue: boolean = false): void {
    if (isTrue) {
      this.filterCol.filterValue = event.target.checked;
    } else {
      this.filterCol.filterValue2 = event.target.checked;
    }
  }

  /**
   * Handle scroll select values
   */
  selectScrollValues(event: any, column: DatatableColumn): void {
    this.onScrollSelect.emit({ event, column });
    column.filterValue = event.map((opt:any) => opt.value);

  }
}
